import { Box, Grid, Tooltip } from '@mui/material';
import classNames from 'classnames';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { FEATURE_DISABLE_EMAIL_SUMMARIES, FEATURE_GAMIFICATION_EMAIL } from '@redux/organization/organizationTypes';
import { getPersonFeatureThunk } from '@redux/person/personThunks';
import { GenderType, Person, PronounType } from '@redux/person/personTypes';
import { Text } from 'components/Typography';
import { Location as PatientLocation } from 'redux/device/deviceTypes';
import { getDeviceModeValue } from 'utils/device';
import { getFormattedPhoneNumber, getInitials } from 'utils/util';
import style from './PatientDetails.scss';

type PropsFromRedux = ConnectedProps<typeof connectRedux>;

type ViewPersonProps = PropsFromRedux & {
  person: Person;
  children?: React.ReactNode;
};

const PatientDetailsComponent: React.FC<ViewPersonProps> = ({ person, children, getPersonFeature }) => {
  const [gamificationStatus, setGamificationStatus] = useState<string>('-');
  const [emailSummariesStatus, setEmailSummariesStatus] = useState<string>('-');
  const personName = person?.fullName ?? '';
  const email = person?.email ?? '';
  const personInitial = getInitials(personName);
  const { title: deviceMode } = getDeviceModeValue({ mode: person?.activeDevice?.deviceMode });

  // Load feature statuses
  useEffect(() => {
    if (person?.id) {
      setGamificationStatus('-');
      setEmailSummariesStatus('-');
      getPersonFeature(person.id, FEATURE_GAMIFICATION_EMAIL)
        .then((response) => {
          const data = response?.data || response;
          setGamificationStatus(data?.disabled ? 'Disabled' : 'Enabled');
        })
        .catch(() => {
          setGamificationStatus('Enabled');
        });
      getPersonFeature(person.id, FEATURE_DISABLE_EMAIL_SUMMARIES)
        .then((response) => {
          const data = response?.data || response;
          setEmailSummariesStatus(data?.disabled ? 'Yes' : 'No');
        })
        .catch(() => {
          setEmailSummariesStatus('No');
        });
    } else {
      setGamificationStatus('-');
      setEmailSummariesStatus('No');
    }
  }, [person?.id, person?.modifiedDate, getPersonFeature]);

  const getAddressLine = (address: PatientLocation) => {
    return [address.street, address.streetSecondLine, address.city, address.state, address.postalCode].filter(Boolean).join(', ');
  };

  const fieldValues = {
    'Date of birth': person?.birthDate ? moment(person.birthDate).format('MM/DD/YYYY') : '-',
    'Phone number': person?.phoneNumber ? getFormattedPhoneNumber(person.phoneNumber) : '-',
    Gender: person?.genderType ? GenderType[person.genderType] : '-',
    Pronouns: person?.pronounType ? PronounType[person.pronounType] : '-',
    Address: (person?.shippingAddress ? getAddressLine(person?.shippingAddress) : null) || '-',
    Nickname: person?.nickname || '-',
    'User Type': person?.patientType || '-',
    Gamification: gamificationStatus,
    'Disable email summaries': emailSummariesStatus,
    'Device mode': deviceMode
  };

  return (
    <div className={style.topContainer}>
      <div className={style.avatarAndText}>
        <div className={style.avatar}>
          <Text variant="2xl" className={style.grayText}>
            {personInitial}
          </Text>
        </div>
        <div className={style.textWrapper}>
          <Text variant="2xl" weight={500} className={style.ellipsisText}>
            {personName}
          </Text>
        </div>
        <Tooltip title={email} followCursor placement="bottom">
          <div className={style.textWrapper}>
            <Text variant="md" className={classNames(style.grayEmailText, style.ellipsisText)}>
              {email}
            </Text>
          </div>
        </Tooltip>
      </div>
      {children}
      <div className={style.patientDetail}>
        <Text variant="xl">Patient details</Text>
      </div>

      <Box sx={{ width: '100%' }}>
        <Grid container rowSpacing={2} columnSpacing={2}>
          {Object.entries(fieldValues).map(([label, value], index) => (
            <Grid size={6} key={label}>
              <Text variant="md">{label}</Text>
              <br />
              <Text variant="md" className={style.grayText}>
                {value}
              </Text>
            </Grid>
          ))}
        </Grid>
      </Box>
    </div>
  );
};

const connectRedux = connect(null, { getPersonFeature: getPersonFeatureThunk });

export const PatientDetails = connectRedux(PatientDetailsComponent);
